import type {CheckoutSession, Order} from '../api/types';
import {isVerifiedPaid, paymentTone} from '../order';
import {isExpired, type CheckoutAttempt} from './checkoutAttempt';

/**
 * What became of a payment, decided in one place so Q9 renders it rather than
 * deciding it.
 *
 * # The rule this file exists to enforce
 *
 * **"A payment exists" is not "a payment succeeded."** ReservePay's checkout
 * object carries no status field; the only progress signal it has is a payment
 * id appearing, and that happens the moment the payer *picks a method* —
 * before any money moves, and identically for a payer who then closes the tab.
 * A screen that read `awaiting_payment` as "nearly there" would be reading a
 * method click as a payment.
 *
 * # And the fact that decides the whole design of Q9
 *
 * **`succeeded` is written by nobody today.** The webhook verifies the
 * signature, re-reads the checkout, confirms ReservePay's own status is exactly
 * `SUCCEEDED`, checks the amount — and then stops, because every write path in
 * the API takes a credential and a webhook has no caller. The bill is left
 * ready to close, waiting for a human (`pokpok-docs/archive/open-questions.md`,
 * "Money — settlement has no honest actor").
 *
 * So the ordinary outcome of a **successful** payment, right now, is
 * [`unconfirmed`] — the same value as an abandoned one. That is not a defect in
 * this file; it is the honest reading of what the system knows, and it is why Q9
 * must not show a spinner that waits for green. The venue-paused decision in
 * 03 §Q9 applies to this too: *a hopeful dead end is worse than a clear one.*
 */
export type PaymentOutcomeKind =
  /** The server confirmed the money. The only value that may be Pandan. */
  | 'verified'
  /**
   * ReservePay took the money and pokpok recorded the checkout as `succeeded`,
   * but the bill is not settled.
   *
   * **Unreachable in the backend as it stands** — nothing writes `succeeded` —
   * and it is here anyway, because the contract defines the value and the day
   * the credential-free settlement path lands it starts arriving. A client that
   * only handled today's behaviour would meet it as a blank screen.
   *
   * It is **not green.** Money the server has not settled is `pending`, however
   * confident anything else is (foundations §4, part 2).
   */
  | 'payment_taken'
  /** ReservePay refused, or the checkout was cancelled. Recoverable: pay again. */
  | 'failed'
  /** The ten minutes ran out. Recoverable, and only by starting a new checkout. */
  | 'expired'
  /** Nothing is known yet. The dominant case, including for a payment that worked. */
  | 'unconfirmed'
  /** Nothing could be read at all — offline, or the session token is gone. */
  | 'unknown';

export interface PaymentOutcome {
  kind: PaymentOutcomeKind;
  /** True when starting a fresh checkout is the honest way forward. */
  canPayAgain: boolean;
}

/**
 * Fold everything the client knows into one outcome.
 *
 * The order is checked **before** the checkout session, deliberately. The order
 * is the authority on money — `orderTotalTone` and `paymentTone` are the only
 * sanctioned paths to Pandan (D-021) — and a checkout session is pokpok's model
 * of a handoff, not a settlement record. If the two ever disagree, the bill
 * wins.
 */
export function paymentOutcome(input: {
  order: Order | null;
  session: CheckoutSession | null;
  attempt: CheckoutAttempt | null;
  now?: number;
}): PaymentOutcome {
  const {order, session, attempt} = input;
  const now = input.now ?? Date.now();

  // 1 · The bill says it is paid. The only route to green on this surface.
  if (order && isVerifiedPaid(order)) return {kind: 'verified', canPayAgain: false};

  // A hosted-checkout payment the server marked `succeeded` is verified money by
  // foundations §4 even when the order has not moved state yet — but it is read
  // through `paymentTone`, which is the sanctioned decider, not by comparing a
  // string here.
  const settledPayment = (order?.payments ?? []).find(
    (payment) =>
      payment.method === 'reservepay_hosted_checkout' && paymentTone(payment) === 'verified',
  );
  if (settledPayment) return {kind: 'verified', canPayAgain: false};

  // 2 · The checkout's own terminal states.
  switch (session?.state) {
    case 'succeeded':
      return {kind: 'payment_taken', canPayAgain: false};
    case 'failed':
    case 'cancelled':
      return {kind: 'failed', canPayAgain: true};
    case 'expired':
      return {kind: 'expired', canPayAgain: true};
    default:
      break;
  }

  // 3 · The window ran out while nothing was written. `expires_at` is the
  // server's field; this never invents a ten-minute countdown of its own.
  if (attempt && isExpired(attempt, now)) return {kind: 'expired', canPayAgain: true};

  // 4 · There is an attempt, and nothing is known about it. The honest answer,
  // and — until settlement has an actor — also the answer for a guest who paid.
  if (attempt || session) return {kind: 'unconfirmed', canPayAgain: true};

  return {kind: 'unknown', canPayAgain: false};
}
